'use client';

import { useDeepMerge } from '@larvitu/shared/react/hooks';
import { Grid } from '@mui/material';
import { cubicBezier, motion } from '../framer-motion-export';
import { Children, type FC, type ReactNode } from 'react';

import { StaggerChildGridItem } from './StaggerChildGridItem';

import type { GridBaseProps, SystemProps } from '@mui/system';
import type { Variants } from 'framer-motion';

type AllGridProps = SystemProps & GridBaseProps;

interface IStaggerChildGridContainer extends AllGridProps {
  children: ReactNode;
  variants?: {
    child?: Variants;
    parent?: Variants;
  };
  itemProps?: AllGridProps;
}

const defaultChildVariants: Variants = {
  start: {
    opacity: 0,
    y: 50,
  },
  end: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: cubicBezier( 0.47, 0, 0.745, 0.715 ),
    },
  },
};

const defaultParentVariants: Variants = {
  end: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

export const StaggerChildGridContainer: FC<IStaggerChildGridContainer> = (
  { children, itemProps, variants, ...props }
) => {
  const newParentVariants = useDeepMerge(
    defaultParentVariants,
    variants?.parent ?? {},
    'overwriteArray'
  );

  const newChildVariants = useDeepMerge(
    defaultChildVariants,
    variants?.child ?? {},
    'overwriteArray'
  );

  return (
    <Grid
      container
      component={motion.div}
      variants={newParentVariants}
      initial="start"
      whileInView="end"
      {...props}
    >
      {Children.map(
        children,
        (component, index) => (
          <StaggerChildGridItem
            key={index}
            variants={newChildVariants}
            {...itemProps}
          >
            {component}
          </StaggerChildGridItem>
        )
      )}
    </Grid>
  );
};
